import { useNavigate } from 'react-router-dom';
import { Button } from '@lex/ui';
import { useAuth } from '../../lib/auth';
import { CheckCircleIcon, PersonIcon, ShieldIcon } from '../icons';
import { Glow, SectionHeader } from './primitives';

interface AudienceCardProps {
  variant: 'accent' | 'ink';
  tag: string;
  title: string;
  questions: string[];
  ctaLabel: string;
}

function AudienceCard({ variant, tag, title, questions, ctaLabel }: AudienceCardProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const isAccent = variant === 'accent';

  return (
    <div
      className={`flex flex-col gap-6 rounded-3xl p-7 md:p-10 ${
        isAccent
          ? 'bg-[linear-gradient(126.87deg,#2B6BFF_0%,#1A40BF_71.43%)]'
          : 'bg-ink-gradient'
      }`}
    >
      <div className="flex items-center gap-3.5">
        <span
          className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border ${
            isAccent
              ? 'border-white/45 bg-white/18 text-white'
              : 'border-gold-300/40 bg-gold/16 text-gold-300'
          }`}
        >
          {isAccent ? <PersonIcon className="h-7 w-7" /> : <ShieldIcon className="h-7 w-7" />}
        </span>
        <span
          className={`text-[12px] font-semibold uppercase tracking-[0.08em] md:text-[13px] ${
            isAccent ? 'text-white' : 'text-gold'
          }`}
        >
          {tag}
        </span>
      </div>

      <h3 className="text-[30px] font-semibold leading-[1.15] tracking-[-0.015em] text-white md:text-[40px] md:leading-[46px]">
        {title}
      </h3>

      <ul className="flex flex-col gap-3.5">
        {questions.map((question) => (
          <li key={question} className="flex items-start gap-3">
            <CheckCircleIcon
              className={`mt-0.5 h-6 w-6 shrink-0 ${isAccent ? 'text-white' : 'text-gold'}`}
            />
            <span className="text-[16px] leading-[1.45] text-white md:text-[18px] md:leading-[26px]">
              {question}
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-2">
        <Button
          variant={isAccent ? 'light' : 'accent'}
          size="lg"
          className="w-full rounded-xl sm:w-auto"
          onClick={() => navigate(isAuthenticated ? '/chat' : '/login')}
        >
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
}

export function AudienceSection() {
  return (
    <section id="for-whom" className="scroll-mt-24 relative overflow-hidden px-4 py-14 md:px-8 md:py-20 xl:px-20">
      <Glow color="blue" size={480} opacity={0.16} blur={70} className="left-0 top-40 hidden lg:block" />
      <Glow color="gold" size={360} opacity={0.16} blur={70} className="right-10 top-24 hidden lg:block" />

      <div className="relative mx-auto flex max-w-[1280px] flex-col gap-10 md:gap-12">
        <SectionHeader
          eyebrow="Для кого"
          title="Кому мы помогаем"
          subtitle="Мы работаем с двумя категориями и знаем их специфику — от повестки до выплат и обжалования."
        />

        <div className="grid gap-6 lg:grid-cols-2">
          <AudienceCard
            variant="accent"
            tag="Призывникам"
            title="Призывникам"
            questions={[
              'Как получить отсрочку по здоровью, учёбе или семье',
              'Что делать, если повестка пришла без оснований',
              'Как обжаловать решение призывной комиссии',
              'Какие документы собрать и куда их подать',
            ]}
            ctaLabel="Получить помощь"
          />
          <AudienceCard
            variant="ink"
            tag="Военнослужащим"
            title="Военнослужащим"
            questions={[
              'Какие выплаты и льготы положены по закону',
              'Как оформить отпуск по болезни или состоянию здоровья',
              'Что делать при нарушении прав в части',
              'Как получить статус ветерана боевых действий',
            ]}
            ctaLabel="Получить помощь"
          />
        </div>

        <div className="flex flex-col gap-1.5 rounded-3xl border border-line bg-white p-6 md:px-9 md:py-6">
          <span className="text-[16px] font-semibold text-ink md:text-[18px]">
            Родственникам тоже помогаем
          </span>
          <span className="text-[15px] leading-[1.4] text-muted md:text-base">
            Обратиться можно от имени близкого человека — с его согласия или по доверенности.
          </span>
        </div>
      </div>
    </section>
  );
}
